import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';

import clsx from 'clsx';

import { useEmbla } from '@hooks/use-embla';

import { ImagePreload } from '@components/common/ImagePreload/ImagePreload';
import { Picture } from '@components/common/Picture/Picture';
import { SliderNavigation } from '@components/common/SliderNavigation/SliderNavigation';
import { SliderPagination } from '@components/common/SliderPagination/SliderPagination';
import { ProductGalleryLightbox } from '@components/product/ProductGalleryLightbox/ProductGalleryLightbox';

import styles from './ProductGallerySlider.module.scss';

const START_INDEX = 0;

type ArticlesSliderProps = {
    items: Global.ImageSet[];
    className?: string;
};
export const ProductGallerySlider: FunctionComponent<ArticlesSliderProps> = (props) => {
    const { items, className } = props;

    const {
        scrollPrev,
        scrollSnaps,
        scrollTo,
        selectedIndex,
        scrollNext,
        isPrevButtonEnabled,
        isNextButtonEnabled,
        viewportRef,
        emblaApi,
    } = useEmbla({
        loop: true,
        startIndex: START_INDEX,
        slidesToScroll: 1,
        containScroll: 'trimSnaps',
        align: 'start',
        draggable: items.length > 1,
    });

    const hasMoreThanOneSlide = items.length > 1;

    // region lightbox
    const [isOpenLightbox, setIsOpenLightbox] = useState(false);
    const [activeLightboxSlideIndex, setActiveLightboxSlideIndex] = useState<number>(START_INDEX);

    const handleSlideChanged = useCallback(() => {
        if (emblaApi === undefined) return;

        const currentSlide = emblaApi.selectedScrollSnap();
        setActiveLightboxSlideIndex(currentSlide);
    }, [emblaApi]);

    useEffect(() => {
        if (emblaApi === undefined) return;
        emblaApi.on('select', handleSlideChanged);

        return () => {
            emblaApi.off('select', handleSlideChanged);
        };
    }, [emblaApi, handleSlideChanged]);
    // endregion lightbox

    return (
        <div className={clsx(styles.root, className)}>
            <div className={styles.slider}>
                <div ref={viewportRef} className={styles.viewport}>
                    <div className={styles.sliderContainer}>
                        {items.map((item, index) => (
                            <React.Fragment key={index}>
                                {index === 0 && <ImagePreload sources={item} />}
                                <button
                                    type="button"
                                    className={styles.picture}
                                    onClick={() => setIsOpenLightbox(true)}
                                    aria-label="Открыть изображение"
                                >
                                    <Picture
                                        priority={index === 0}
                                        sources={item}
                                        shouldHaveItemProp
                                    />
                                </button>
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {hasMoreThanOneSlide && (
                    <SliderNavigation
                        onPrev={scrollPrev}
                        onNext={scrollNext}
                        isPrevButtonEnabled={isPrevButtonEnabled}
                        isNextButtonEnabled={isNextButtonEnabled}
                        variation="circle"
                        classes={{
                            next: styles.navigationButtonNext,
                            prev: styles.navigationButtonPrev,
                        }}
                    />
                )}

                {hasMoreThanOneSlide && scrollSnaps.length > 0 && (
                    <SliderPagination
                        scrollSnaps={scrollSnaps}
                        scrollTo={scrollTo}
                        selectedIndex={selectedIndex}
                    />
                )}
            </div>

            <ProductGalleryLightbox
                items={items}
                activeSlideIndex={activeLightboxSlideIndex}
                setActiveSlideIndex={setActiveLightboxSlideIndex}
                isOpen={isOpenLightbox}
                onClose={() => setIsOpenLightbox(false)}
            />
        </div>
    );
};
